import React, { useEffect, useState } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { auth } from "../firebase";
import { API_BASE_URL } from "../config";
import Sidebar from "./Sidebar";
import GeneralInvoiceApprovalControls from "./GeneralInvoiceApprovalControls";

const CLIENT_LABELS = {
  ARSL: "Allied Rental Services Limited",
  ATL: "Allied Transport Limited",
  ATL_COCACOLA: "ATL CocaCola",
  ES: "E-Commerce",
  AESL: "Allied Engineering",
  PTC_AKF: "PTC AKF",
  SEFAM: "Sefam Pvt Ltd (Kayseria)",
  UNITED_DETERGENT: "United Detergent (Unit I)",
  G_SOLAR: "GSolar Power",
  VS_ALPHA: "VS Alpha"
};

const ALM_BLUE = "FF0B3D6D";
const LIGHT_FILL = "FFEAF2FB";
const XLSX_NUM_FMT = "#,##0";
const xlsxMoney = (v) => Math.round(Number(v || 0));
const xlsxThinBorder = { style: "thin", color: { argb: "FFB0BEC9" } };
const xlsxAllBorders = { top: xlsxThinBorder, left: xlsxThinBorder, bottom: xlsxThinBorder, right: xlsxThinBorder };

function xlsxStyleTitle(cell, text, size = 14) {
  cell.value = text;
  cell.font = { bold: true, size, color: { argb: ALM_BLUE } };
}
function xlsxStyleHeaderRow(row) {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALM_BLUE } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = xlsxAllBorders;
  });
  row.height = 28;
}
function xlsxStyleDataRow(row, { shaded = false, bold = false } = {}) {
  row.eachCell((cell) => {
    cell.border = xlsxAllBorders;
    cell.alignment = { vertical: "middle" };
    if (shaded) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT_FILL } };
    if (bold) cell.font = { bold: true };
  });
}

function Dashboard() {
  const [client] = useState(() => new URLSearchParams(window.location.search).get("client") || "");
  const [files, setFiles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceMonth, setInvoiceMonth] = useState("");
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("");
  const [invoiceNumberEdited, setInvoiceNumberEdited] = useState(false);

  const format = (num) => Math.round(Number(num || 0)).toLocaleString("en-PK");
  const totalInvoiceValue = invoices.reduce(
    (sum, invoice) => sum + Number(invoice.grandTotal || invoice.summary?.grandTotal || 0),
    0
  );

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const loadNextInvoiceNumber = async () => {
    const token = await getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/invoices/next-number/${client || "ARM"}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok) {
        setNextInvoiceNumber(result.invoiceNumber || "");
        setInvoiceNumberEdited(false);
      }
    } catch (error) {
      console.warn("Could not load next invoice number:", error.message);
    }
  };

  const getToken = async () => {
    const user = auth.currentUser;

    if (!user) {
      alert("Not logged in.");
      return null;
    }

    return user.getIdToken();
  };

  useEffect(() => {
    loadNextInvoiceNumber();
  }, []);

  const uploadFiles = async () => {
    if (!files.length) {
      alert("Please select Excel files first.");
      return;
    }

    const token = await getToken();
    if (!token) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    if (invoiceNumberEdited && nextInvoiceNumber.trim()) {
      formData.append("invoiceNumber", nextInvoiceNumber.trim());
    }
    if (client) formData.append("client", client);

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/invoices/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || "Invoice generation failed.");
        return;
      }

      setInvoices(result.invoices || []);
      if (result.detectedInvoiceMonth && !invoiceMonth.trim()) {
        setInvoiceMonth(result.detectedInvoiceMonth);
      }
      await loadNextInvoiceNumber();
    } finally {
      setLoading(false);
    }
  };

  const addResourceCostInvoice = async () => {
    if (!invoiceMonth.trim()) {
      alert("Enter the Invoice Month first — it's used in the invoice description.");
      return;
    }

    const token = await getToken();
    if (!token) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/invoices/resource-cost`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceMonth })
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.error || "Resource cost invoice could not be generated.");
        return;
      }

      setInvoices((current) => [...current, result.invoice]);
      await loadNextInvoiceNumber();
    } finally {
      setLoading(false);
    }
  };

  const downloadUdWorkbook = async () => {
    const payroll = invoices.find((inv) => inv.type === "ud-payroll");
    const statutory = invoices.find((inv) => inv.type === "ud-statutory");
    if (!payroll && !statutory) {
      alert("Generate United Detergents invoices first.");
      return;
    }

    const ALM_BLUE = "FF0B3D6D";
    const LIGHT_FILL = "FFEAF2FB";
    const money = (v) => Math.round(Number(v || 0));
    const thinBorder = { style: "thin", color: { argb: "FFB0BEC9" } };
    const allBorders = { top: thinBorder, left: thinBorder, bottom: thinBorder, right: thinBorder };

    const styleTitle = (cell, text, size = 14) => {
      cell.value = text;
      cell.font = { bold: true, size, color: { argb: ALM_BLUE } };
    };
    const styleHeaderRow = (row) => {
      row.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALM_BLUE } };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = allBorders;
      });
      row.height = 28;
    };
    const styleDataRow = (row, { shaded = false, bold = false } = {}) => {
      row.eachCell((cell) => {
        cell.border = allBorders;
        cell.alignment = { vertical: "middle" };
        if (shaded) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT_FILL } };
        if (bold) cell.font = { bold: true };
      });
    };
    const numFmt = "#,##0";

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "ALM Outsourcing Services (Pvt) Ltd.";
    workbook.created = new Date();

    // ---- Invoice-formatted sheets, matching the PDFs sent to the client ----
    if (payroll) {
      const sheet = workbook.addWorksheet("Invoice - Payroll");
      sheet.columns = [{ width: 6 }, { width: 42 }, { width: 14 }, { width: 18 }];
      styleTitle(sheet.getCell("A1"), "ALM Outsourcing Services (Pvt) Ltd.");
      sheet.mergeCells("A1:D1");
      sheet.getCell("A2").value = `Invoice # ${payroll.invoiceNumber || ""}    Date: ${new Date().toLocaleDateString("en-GB")}`;
      sheet.mergeCells("A2:D2");
      sheet.getCell("A3").value = `RE: SALARIES FOR THE MONTH OF ${invoiceMonth || ""} (${payroll.unit || "Unit I"})`;
      sheet.getCell("A3").font = { bold: true };
      sheet.mergeCells("A3:D3");
      sheet.addRow([]);

      const header = sheet.addRow(["Sr No", "Description", "Head Count", "G. Total"]);
      styleHeaderRow(header);

      const r1 = sheet.addRow([1, "Monthly Salaries Reimbursment", payroll.headCount, money(payroll.monthlySalaries)]);
      styleDataRow(r1);
      const r2 = sheet.addRow([2, "Loading & Unloading Bill Reimbursment", "", payroll.loadingUnloading ? money(payroll.loadingUnloading) : "-"]);
      styleDataRow(r2);
      const sub1 = sheet.addRow(["", "Subtotal", "", money(payroll.payrollSubtotal)]);
      styleDataRow(sub1, { shaded: true, bold: true });

      const r3 = sheet.addRow([3, "Services Charges @ 6%", "", money(payroll.serviceCharges)]);
      styleDataRow(r3);
      const r4 = sheet.addRow([4, "GST on Service Charges @ 16%", "", money(payroll.gst)]);
      styleDataRow(r4);
      const sub2 = sheet.addRow(["", "Subtotal", "", money(payroll.serviceCharges + payroll.gst)]);
      styleDataRow(sub2, { shaded: true, bold: true });

      sheet.addRow([]);
      const grand = sheet.addRow(["", "Grand Total / Net Payable", "", money(payroll.grandTotal)]);
      styleDataRow(grand, { bold: true });
      grand.eachCell((cell) => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALM_BLUE } }; cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; });
      sheet.getColumn(4).numFmt = numFmt;
      sheet.getColumn(3).alignment = { horizontal: "center" };
    }

    if (statutory) {
      const sheet = workbook.addWorksheet("Invoice - EOBI & PESSI");
      sheet.columns = [{ width: 6 }, { width: 42 }, { width: 14 }, { width: 18 }];
      styleTitle(sheet.getCell("A1"), "ALM Outsourcing Services (Pvt) Ltd.");
      sheet.mergeCells("A1:D1");
      sheet.getCell("A2").value = `Invoice # ${statutory.invoiceNumber || ""}    Date: ${new Date().toLocaleDateString("en-GB")}`;
      sheet.mergeCells("A2:D2");
      sheet.getCell("A3").value = `RE: SALARIES FOR THE MONTH OF ${invoiceMonth || ""} (${statutory.unit || "Unit I"})`;
      sheet.getCell("A3").font = { bold: true };
      sheet.mergeCells("A3:D3");
      sheet.addRow([]);

      const header = sheet.addRow(["Sr No", "Description", "Head Count", "G. Total"]);
      styleHeaderRow(header);

      styleDataRow(sheet.addRow([1, "Resource Cost Reimbursment", statutory.resourceCostHeadCount, money(statutory.resourceCostTotal)]));
      styleDataRow(sheet.addRow([2, "EOBI Reimbursment", "", money(statutory.eobiTotal)]));
      styleDataRow(sheet.addRow([3, "PESSI Reimbursment", "", money(statutory.pessiTotal)]));
      styleDataRow(sheet.addRow(["", "Subtotal", "", money(statutory.grandTotal)]), { shaded: true, bold: true });

      sheet.addRow([]);
      const grand = sheet.addRow(["", "Grand Total / Net Payable", "", money(statutory.grandTotal)]);
      styleDataRow(grand, { bold: true });
      grand.eachCell((cell) => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALM_BLUE } }; cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; });
      sheet.getColumn(4).numFmt = numFmt;
      sheet.getColumn(3).alignment = { horizontal: "center" };
    }

    // ---- Payroll Detail ----
    if (payroll) {
      const sheet = workbook.addWorksheet("Payroll Detail");
      const emps = payroll.employees || [];
      const sum = (field) => emps.reduce((s, e) => s + Number(e[field] || 0), 0);

      styleTitle(sheet.getCell("A1"), `United Detergents — ${payroll.unit || "Unit I"} — Payroll Detail (${invoiceMonth || ""})`, 12);
      sheet.mergeCells("A1:V1");
      sheet.addRow([]);

      const columns = [
        ["Sr.No", 6], ["Emp Code", 10], ["Name", 22], ["Department", 14], ["CNIC", 16], ["Monthly Salary", 14],
        ["Basic 60%", 12], ["House Rent 27%", 13], ["Utility 6%", 11], ["Medical 4%", 11], ["Conveyance 3%", 13],
        ["Working Days", 12], ["OT Hours", 9], ["OT Amount", 11], ["Salary Earned", 13],
        ["Other Deductions", 14], ["Arrears", 10], ["EOBI (Employee 1%)", 15], ["Food/Mess Deduction", 15], ["Payable Amount (Net)", 16],
        ["EOBI (Employer)", 13], ["PESSI", 11]
      ];
      sheet.columns = columns.map(([, width]) => ({ width }));
      const header = sheet.addRow(columns.map(([label]) => label));
      styleHeaderRow(header);

      emps.forEach((e, i) => {
        const row = sheet.addRow([
          i + 1, e.empCode, e.name, e.department, e.cnic, e.nominalSalary,
          e.basic, e.houseRent, e.utility, e.medical, e.conveyance,
          e.workingDays, e.otHours, e.otAmount, e.salaryEarned,
          e.otherDeduction, e.arrears, e.employeeEobiDeduction, e.foodDeduction, e.payableAmount,
          e.eobi, e.pessi
        ]);
        styleDataRow(row, { shaded: i % 2 === 1 });
      });

      const totalRow = sheet.addRow([
        "", "", "", "", "Total", sum("nominalSalary"),
        sum("basic"), sum("houseRent"), sum("utility"), sum("medical"), sum("conveyance"),
        "", "", sum("otAmount"), sum("salaryEarned"),
        sum("otherDeduction"), sum("arrears"), sum("employeeEobiDeduction"), sum("foodDeduction"), sum("payableAmount"),
        sum("eobi"), sum("pessi")
      ]);
      styleDataRow(totalRow, { bold: true, shaded: true });
      [6, 7, 8, 9, 10, 11, 14, 15, 16, 17, 18, 19, 20, 21, 22].forEach((col) => { sheet.getColumn(col).numFmt = numFmt; });
      sheet.views = [{ state: "frozen", ySplit: 3 }];
    }

    // ---- Resource Cost ----
    if (statutory?.resourceCost?.length) {
      const sheet = workbook.addWorksheet("Resource Cost");
      const rcColumns = [
        ["Sr.No", 6], ["Name", 20], ["Designation", 22], ["ALM Emp ID", 12], ["Salary", 12],
        ["Working Days", 12], ["OT Hours", 9], ["Salary Earned", 13], ["Overtime Amount", 14],
        ["Arrears", 10], ["Total Earnings", 13], ["Net Salary", 13]
      ];
      sheet.columns = rcColumns.map(([, width]) => ({ width }));
      styleTitle(sheet.getCell("A1"), "Dedicated Resource Cost", 12);
      sheet.mergeCells(`A1:${String.fromCharCode(64 + rcColumns.length)}1`);
      sheet.addRow([]);
      const header = sheet.addRow(rcColumns.map(([label]) => label));
      styleHeaderRow(header);

      const rc = statutory.resourceCost;
      rc.forEach((e, i) => {
        const row = sheet.addRow([
          i + 1, e.name, e.designation, e.empId, e.nominalSalary,
          e.workingDays, e.otHours, e.salaryEarned, e.overtimeAmount,
          e.arrears, e.totalEarnings, e.netSalary
        ]);
        styleDataRow(row, { shaded: i % 2 === 1 });
      });
      const rcSum = (field) => rc.reduce((s, e) => s + Number(e[field] || 0), 0);
      styleDataRow(sheet.addRow([
        "", "", "", "Total", rcSum("nominalSalary"),
        "", "", rcSum("salaryEarned"), rcSum("overtimeAmount"),
        rcSum("arrears"), rcSum("totalEarnings"), rcSum("netSalary")
      ]), { bold: true, shaded: true });
      [5, 8, 9, 10, 11, 12].forEach((col) => { sheet.getColumn(col).numFmt = numFmt; });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `UD_${(invoiceMonth || "invoice").replaceAll(" ", "_")}.xlsx`);
  };

  const downloadGSolarWorkbook = async () => {
    const gsolarInvoices = invoices.filter((inv) => inv.type === "gsolar-invoice");
    if (!gsolarInvoices.length) {
      alert("Generate the GSolar / VS Alpha invoice first.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "ALM Outsourcing Services (Pvt) Ltd.";
    workbook.created = new Date();

    let logoImageId = null;
    try {
      const logoBuffer = await fetch("/alm-logo.png").then((r) => r.arrayBuffer());
      logoImageId = workbook.addImage({ buffer: logoBuffer, extension: "png" });
    } catch {
      logoImageId = null;
    }

    gsolarInvoices.forEach((invoice) => {
      const companyLabel = CLIENT_LABELS[invoice.company] || invoice.company;

      // ---- Invoice-formatted sheet, matching the PDF sent to the client ----
      const sheet = workbook.addWorksheet(`Invoice - ${companyLabel}`.slice(0, 31));
      sheet.columns = [{ width: 34 }, { width: 18 }, { width: 16 }, { width: 18 }];
      if (logoImageId !== null) {
        sheet.addImage(logoImageId, { tl: { col: 0, row: 0 }, ext: { width: 110, height: 55 } });
      }
      sheet.getRow(1).height = 42;
      sheet.getRow(2).height = 18;

      const banner = sheet.getCell("A4");
      banner.value = "Sindh Sales Tax";
      sheet.mergeCells("A4:D4");
      banner.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      banner.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALM_BLUE } };
      banner.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(4).height = 24;

      sheet.getCell("A6").value = `Invoice # ${invoice.invoiceNumber || ""}    Date: ${new Date().toLocaleDateString("en-GB")}`;
      sheet.mergeCells("A6:D6");
      sheet.getCell("A7").value = `Services To: ${companyLabel}`;
      sheet.getCell("A7").font = { bold: true };
      sheet.mergeCells("A7:D7");
      sheet.getCell("A8").value = `RE: SERVICES FOR THE MONTH OF ${invoiceMonth || ""}`;
      sheet.getCell("A8").font = { bold: true };
      sheet.mergeCells("A8:D8");
      sheet.addRow([]);

      const header = sheet.addRow(["Description", "Invoice Amount", "Sales Tax @15%", "Total Amount"]);
      xlsxStyleHeaderRow(header);

      const line1 = sheet.addRow([companyLabel, xlsxMoney(invoice.totalCostSum), "", xlsxMoney(invoice.totalCostSum)]);
      xlsxStyleDataRow(line1);
      const line2 = sheet.addRow([companyLabel, xlsxMoney(invoice.serviceChargeSum), xlsxMoney(invoice.gst), xlsxMoney(invoice.serviceChargeSum + invoice.gst)]);
      xlsxStyleDataRow(line2);

      sheet.addRow([]);
      const grand = sheet.addRow(["", "", "Grand Total", xlsxMoney(invoice.grandTotal)]);
      xlsxStyleDataRow(grand, { bold: true });
      grand.eachCell((cell) => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALM_BLUE } }; cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; });
      [2, 3, 4].forEach((col) => { sheet.getColumn(col).numFmt = XLSX_NUM_FMT; sheet.getColumn(col).alignment = { horizontal: "right" }; });

      // ---- Employee Detail sheet ----
      const detail = workbook.addWorksheet(`Detail - ${companyLabel}`.slice(0, 31));
      if (logoImageId !== null) {
        detail.addImage(logoImageId, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 50 } });
      }
      detail.getRow(1).height = 40;
      xlsxStyleTitle(detail.getCell("C1"), `${companyLabel} — Detail (${invoiceMonth || ""})`, 12);

      const columns = [
        ["Sr.No", 6], ["Emp Code", 12], ["Name", 24], ["DBA", 24], ["Staff Category", 16],
        ["Basic Salary", 13], ["Days Worked", 12], ["Per Day Rate", 12], ["Payable Salary", 14],
        ["PPE", 9], ["EOBI", 9], ["Health Insurance", 15], ["GLI", 9], ["Total Cost", 13],
        ["Service Charge", 14], ["Invoice Amount", 14]
      ];
      detail.columns = columns.map(([, width]) => ({ width }));
      const detailHeader = detail.addRow(columns.map(([label]) => label));
      xlsxStyleHeaderRow(detailHeader);

      const emps = invoice.employees || [];
      emps.forEach((e, i) => {
        const row = detail.addRow([
          i + 1, e.empCode, e.name, e.dba, e.staffCategory,
          e.basicSalary, e.daysWorked, e.perDayRate, e.payableSalary,
          e.ppe, e.eobi, e.healthInsurance, e.gli, e.totalCost,
          e.serviceCharge, e.invoiceAmount
        ]);
        xlsxStyleDataRow(row, { shaded: i % 2 === 1 });
      });

      const dsum = (field) => emps.reduce((s, e) => s + Number(e[field] || 0), 0);
      const detailTotal = detail.addRow([
        "", "", "", "", "Total",
        dsum("basicSalary"), "", "", dsum("payableSalary"),
        dsum("ppe"), dsum("eobi"), dsum("healthInsurance"), dsum("gli"), dsum("totalCost"),
        dsum("serviceCharge"), dsum("invoiceAmount")
      ]);
      xlsxStyleDataRow(detailTotal, { bold: true, shaded: true });
      [6, 8, 9, 10, 11, 12, 13, 14, 15, 16].forEach((col) => { detail.getColumn(col).numFmt = XLSX_NUM_FMT; });
      detail.views = [{ state: "frozen", ySplit: 2 }];
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `GSolar_${(invoiceMonth || "invoice").replaceAll(" ", "_")}.xlsx`);
  };

  const downloadInvoice = async (invoice) => {
    const token = await getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/invoices/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          invoiceNumber: invoice.invoiceNumber,
          company: invoice.company,
          invoiceType: invoice.type,
          segment: invoiceMonth,
          department: invoice.department,
          rows: invoice.rows,
          summary: invoice.summary,
          headCount: invoice.headCount,
          serviceAmount: invoice.serviceAmount,
          gst: invoice.gst,
          grandTotal: invoice.grandTotal,
          grossWages: invoice.grossWages,
          workingDays: invoice.workingDays,
          ratePerDay: invoice.ratePerDay,
          unit: invoice.unit,
          monthLabel: invoiceMonth,
          monthlySalaries: invoice.monthlySalaries,
          loadingUnloading: invoice.loadingUnloading,
          payrollSubtotal: invoice.payrollSubtotal,
          serviceCharges: invoice.serviceCharges,
          resourceCostTotal: invoice.resourceCostTotal,
          resourceCostHeadCount: invoice.resourceCostHeadCount,
          eobiTotal: invoice.eobiTotal,
          pessiTotal: invoice.pessiTotal,
          totalCostSum: invoice.totalCostSum,
          serviceChargeSum: invoice.serviceChargeSum
        })
      });

      if (!response.ok) {
        const message = await response.text();
        alert(message || "PDF generation failed.");
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `${invoice.invoiceNumber.replaceAll("/", "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(`PDF download failed: ${error.message}`);
    }
  };

  const downloadAll = async () => {
    if (!invoices.length) {
      alert("No invoices to download.");
      return;
    }

    const token = await getToken();
    if (!token) return;

    const zip = new JSZip();

    for (const invoice of invoices) {
      const response = await fetch(`${API_BASE_URL}/invoices/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          invoiceNumber: invoice.invoiceNumber,
          company: invoice.company,
          invoiceType: invoice.type,
          segment: invoiceMonth,
          department: invoice.department,
          rows: invoice.rows,
          summary: invoice.summary,
          headCount: invoice.headCount,
          serviceAmount: invoice.serviceAmount,
          gst: invoice.gst,
          grandTotal: invoice.grandTotal,
          grossWages: invoice.grossWages,
          workingDays: invoice.workingDays,
          ratePerDay: invoice.ratePerDay,
          unit: invoice.unit,
          monthLabel: invoiceMonth,
          monthlySalaries: invoice.monthlySalaries,
          loadingUnloading: invoice.loadingUnloading,
          payrollSubtotal: invoice.payrollSubtotal,
          serviceCharges: invoice.serviceCharges,
          resourceCostTotal: invoice.resourceCostTotal,
          resourceCostHeadCount: invoice.resourceCostHeadCount,
          eobiTotal: invoice.eobiTotal,
          pessiTotal: invoice.pessiTotal,
          totalCostSum: invoice.totalCostSum,
          serviceChargeSum: invoice.serviceChargeSum
        })
      });

      if (!response.ok) continue;

      const blob = await response.blob();
      zip.file(`${invoice.invoiceNumber.replaceAll("/", "_")}.pdf`, blob);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, `Invoices_${invoiceMonth || "batch"}.zip`);
  };

  return (
    <div className="app-shell">
      <Sidebar />

      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Invoice Operations</div>
            <h1 className="page-title">{client ? CLIENT_LABELS[client] || client : "Corporate Invoice Engine"}</h1>
            <p className="page-copy">
              Upload client workbooks, review generated invoices, and download
              polished PDF packs for ALM Outsourcing Services.
            </p>
          </div>
        </header>

        <section className="card-grid">
          <div className="metric-card">
            <div className="metric-label">Generated Invoices</div>
            <div className="metric-value">{invoices.length}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Last Invoice</div>
            <div className="metric-value">
              {invoices.length ? invoices[invoices.length - 1].invoiceNumber : "None"}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Batch Value</div>
            <div className="metric-value">PKR {format(totalInvoiceValue)}</div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Generate Invoice Batch</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                Select one or more Excel files and enter the month to appear on the PDF invoices.
              </p>
            </div>
            <span className="badge">XLSX</span>
          </div>

          <div className="panel-body">
            <label htmlFor="invoice-files" className="file-drop">
              <div className="file-title">
                {files.length ? `${files.length} file(s) selected` : "Choose invoice workbooks"}
              </div>
              <div className="file-copy">
                {files.length
                  ? files.map((file) => file.name).join(", ")
                  : client
                    ? `Upload the ${CLIENT_LABELS[client] || client} Excel workbook(s)`
                    : "Upload AE, ES, ATL, or ARSL Excel files"}
              </div>
            </label>
            <input
              id="invoice-files"
              type="file"
              accept=".xlsx"
              multiple
              onChange={handleFileChange}
              className="hidden-input"
            />

            <div className="form-grid" style={{ marginTop: 18 }}>
              <div className="field">
                <label>Next Invoice Number</label>
                <input
                  className="input"
                  placeholder="e.g. 2027-256/ARM"
                  value={nextInvoiceNumber}
                  onChange={(e) => {
                    setNextInvoiceNumber(e.target.value);
                    setInvoiceNumberEdited(true);
                  }}
                />
                <p className="page-copy" style={{ marginTop: 4 }}>
                  Loaded from Firestore. Edit this once if you need to reset the starting number; the system will remember the new series after generation.
                </p>
              </div>
              <div className="field">
                <label>Invoice Month</label>
                <input
                  className="input"
                  placeholder="e.g. Feb-2027"
                  value={invoiceMonth}
                  onChange={(e) => setInvoiceMonth(e.target.value)}
                />
              </div>
            </div>

            <div className="button-row">
              <button
                type="button"
                onClick={downloadAll}
                className="btn btn-success"
                disabled={!invoices.length}
              >
                Download All PDFs
              </button>
              <button
                type="button"
                onClick={uploadFiles}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? "Processing..." : "Generate Invoices"}
              </button>
              {(!client || client === "ARSL") && (
                <button
                  type="button"
                  onClick={addResourceCostInvoice}
                  disabled={loading}
                  className="btn btn-muted"
                  title="Adds a flat PKR 42,000 reimbursement invoice for ARSL — no service charge, no GST, no WHT."
                >
                  Add BSPM Resource Cost Invoice (ARSL)
                </button>
              )}
              {(client === "UNITED_DETERGENT" || invoices.some((inv) => inv.type === "ud-payroll" || inv.type === "ud-statutory")) && (
                <button
                  type="button"
                  onClick={downloadUdWorkbook}
                  disabled={!invoices.some((inv) => inv.type === "ud-payroll" || inv.type === "ud-statutory")}
                  className="btn btn-muted"
                >
                  Download UD Workbook (Excel)
                </button>
              )}
              {(client === "G_SOLAR" || client === "VS_ALPHA" || invoices.some((inv) => inv.type === "gsolar-invoice")) && (
                <button
                  type="button"
                  onClick={downloadGSolarWorkbook}
                  disabled={!invoices.some((inv) => inv.type === "gsolar-invoice")}
                  className="btn btn-muted"
                >
                  Download GSolar Workbook (Excel)
                </button>
              )}
            </div>
          </div>
        </section>

        {invoices.length === 0 ? (
          <div className="empty-state">
            <h3 style={{ margin: 0 }}>No invoices generated yet</h3>
            <p className="page-copy" style={{ margin: "8px auto 0" }}>
              Generated invoices will appear here with PDF download controls and invoice previews.
            </p>
          </div>
        ) : (
          <section className="invoice-list">
            {invoices.map((invoice, index) => (
              <article className="invoice-card" key={`${invoice.invoiceNumber}-${index}`}>
                <div className="invoice-card-header">
                  <div>
                    <h3 className="invoice-card-title">
                      {invoice.company} - {invoice.invoiceNumber}
                    </h3>
                    <span className="badge">{invoice.type}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadInvoice(invoice)}
                    className="btn btn-primary"
                  >
                    Download PDF
                  </button>
                </div>

                {invoice.type === "reimbursement" && (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Employees</th>
                          <th>Region</th>
                          <th>Salary</th>
                          <th>Allowance</th>
                          <th>Total</th>
                          <th>EOBI</th>
                          <th>PESSI</th>
                          <th>G.Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.rows?.map((row, i) => (
                          <tr key={i}>
                            <td>{row.segment}</td>
                            <td>{row.employees}</td>
                            <td>{row.region}</td>
                            <td>{format(row.salary)}</td>
                            <td>{format(row.allowance)}</td>
                            <td>{format(row.total)}</td>
                            <td>{format(row.eobi)}</td>
                            <td>{format(row.pessi)}</td>
                            <td><strong>{format(row.grandTotal)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {invoice.type === "service" && (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Sr No</th>
                          <th>Description</th>
                          <th>Head Count</th>
                          <th>G. Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>1</td>
                          <td>Service Charges</td>
                          <td>{invoice.headCount}</td>
                          <td>{format(invoice.serviceAmount)}</td>
                        </tr>
                        <tr>
                          <td>2</td>
                          <td>GST on Service Charges</td>
                          <td></td>
                          <td>{format(invoice.gst)}</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td><strong>Subtotal</strong></td>
                          <td></td>
                          <td><strong>{format(invoice.grandTotal)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {invoice.type === "ud-payroll" && (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Sr No</th><th>Description</th><th>Head Count</th><th>G. Total</th></tr></thead>
                      <tbody>
                        <tr><td>1</td><td>Monthly Salaries Reimbursment</td><td>{invoice.headCount}</td><td>{format(invoice.monthlySalaries)}</td></tr>
                        <tr><td>2</td><td>Loading &amp; Unloading Bill Reimbursment</td><td></td><td>{invoice.loadingUnloading ? format(invoice.loadingUnloading) : "-"}</td></tr>
                        <tr><td></td><td><strong>Subtotal</strong></td><td></td><td><strong>{format(invoice.payrollSubtotal)}</strong></td></tr>
                        <tr><td>3</td><td>Services Charges @ 6%</td><td></td><td>{format(invoice.serviceCharges)}</td></tr>
                        <tr><td>4</td><td>GST on Service Charges @ 16%</td><td></td><td>{format(invoice.gst)}</td></tr>
                        <tr><td></td><td><strong>Grand Total</strong></td><td></td><td><strong>{format(invoice.grandTotal)}</strong></td></tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {invoice.type === "ud-statutory" && (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Sr No</th><th>Description</th><th>Head Count</th><th>G. Total</th></tr></thead>
                      <tbody>
                        <tr><td>1</td><td>Resource Cost Reimbursment</td><td>{invoice.resourceCostHeadCount}</td><td>{format(invoice.resourceCostTotal)}</td></tr>
                        <tr><td>2</td><td>EOBI Reimbursment</td><td></td><td>{format(invoice.eobiTotal)}</td></tr>
                        <tr><td>3</td><td>PESSI Reimbursment</td><td></td><td>{format(invoice.pessiTotal)}</td></tr>
                        <tr><td></td><td><strong>Grand Total</strong></td><td></td><td><strong>{format(invoice.grandTotal)}</strong></td></tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {invoice.type === "gsolar-invoice" && (
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Description</th><th>Invoice Amount</th><th>Sales Tax @15%</th><th>Total Amount</th></tr></thead>
                      <tbody>
                        <tr><td>{CLIENT_LABELS[invoice.company] || invoice.company}</td><td>{format(invoice.totalCostSum)}</td><td></td><td>{format(invoice.totalCostSum)}</td></tr>
                        <tr><td>{CLIENT_LABELS[invoice.company] || invoice.company}</td><td>{format(invoice.serviceChargeSum)}</td><td>{format(invoice.gst)}</td><td>{format(invoice.serviceChargeSum + invoice.gst)}</td></tr>
                        <tr><td></td><td></td><td><strong>Grand Total</strong></td><td><strong>{format(invoice.grandTotal)}</strong></td></tr>
                      </tbody>
                    </table>
                  </div>
                )}
                <GeneralInvoiceApprovalControls invoice={invoice} invoiceMonth={invoiceMonth} />
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
